import React, { useMemo } from 'react';
import styled from 'styled-components';

import { formatDate } from '../../../../utils/date/date';

type HistoryType = {
  status: any;
  background?: string;
  isNightManager?: boolean;
  startPercent: number;
  endPercent: number;
  startedAt?: Date;
  endAt?: Date;
};

type HistoryItemProps = {
  startPercent: number;
  endPercent: number;
  color?: string;
};

interface IManagerStatusHistory {
  className?: string;
  list: HistoryType[];
}

const ManagerStatusHistory = ({
  className = '',
  list = [],
}: IManagerStatusHistory) => {
  const HistoryMap = useMemo(() => {
    return list.map(
      ({ startPercent, endPercent, startedAt, endAt, status }) => {
        return (
          <StyledHistoryItem
            color={status?.color}
            startPercent={startPercent}
            endPercent={endPercent}
            data-title={`${status?.title} ${formatDate(
              startedAt,
              'H:mm'
            )} — ${formatDate(endAt, 'H:mm')}`}
          />
        );
      }
    );
  }, [list]);

  const Separators = useMemo(
    () => (
      <StyledSeparators>
        {Array.from({ length: 24 }).map((_, index) => (
          <StyledSeparatorItem key={index} />
        ))}
      </StyledSeparators>
    ),
    []
  );

  const Timeline = useMemo(
    () => (
      <StyledTimeline>
        {Array.from({ length: 24 }).map((_, index) => (
          <StyledTimeItem>
            {index < 10 && '0'}
            {index}:00
          </StyledTimeItem>
        ))}
      </StyledTimeline>
    ),
    []
  );

  return (
    <StyledManagerStatusHistory className={className}>
      {Timeline}
      {Separators}
      {HistoryMap}
    </StyledManagerStatusHistory>
  );
};

const StyledManagerStatusHistory = styled.div`
  border: 1px solid #c2c5cc;
  position: relative;
  display: flex;
  height: 38px;
  width: 360px;
  flex-direction: column;
  border-radius: 4px;
`;

const StyledTimeline = styled.div`
  color: #939393;
  font-size: 10px;
  position: absolute;
  bottom: 0px;
  left: 4px;
  display: flex;
  width: calc(100% - 8px);
  -moz-box-pack: justify;
  justify-content: space-between;
  font-family: Monospace;
`;

const StyledTimeItem = styled.div``;

const StyledSeparators = styled.div`
  position: absolute;
  width: 100%;
  display: flex;
  top: 50%;
  margin-top: -5px;
`;

const StyledSeparatorItem = styled.div`
  width: calc(100% / 11);
  height: 10px;
  border-left: 1px solid #c2c5cc;

  :first-child {
    border-left: none;
  }
`;

const StyledHistoryItem = styled.div.withConfig({
  shouldForwardProp: prop =>
    !['startPercent', 'endPercent', 'color'].includes(prop),
})<HistoryItemProps>`
  position: absolute;
  left: ${props => props.startPercent}%;
  right: ${props => `${100 - props.endPercent}%`};
  background: ${props => props.color || '#d0d0d0'};
  top: 4px;
  height: 16px;
  min-width: 1px;
  border-radius: 4px;
  opacity: 0.8;

  &[data-title]::after {
    content: attr(data-title);
    white-space: pre;
    opacity: 0;
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #ffd49e;
    padding: 4px;
    border-radius: 4px;
    font-weight: bold;
  }

  &:hover {
    &[data-title]::after {
      opacity: 1;
    }
  }
`;

export default ManagerStatusHistory;
